import { useEffect, useRef, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import { brand, fontFamily, tg } from '../../theme/tokens';
import {
  validatePhotoSync,
  validatePhotoAsync,
  type PhotoError,
} from '../../lib/photoValidation';

interface PhotoSlotProps {
  /** Slot label rendered below the photo (e.g. "Female" / "Male"). */
  label: string;
  /** Selected file, or null for empty / error state. */
  file: File | null;
  /** Fired when the slot's file changes (accepted, removed, or replaced). */
  onFileSelect: (file: File | null) => void;
  /** Fired with a validation error for the slot, or null to clear. Parent
   *  uses this to gate the Continue CTA and render the explanatory banner. */
  onError: (error: PhotoError | null) => void;
  /** Current error to render (kept in parent — single source of truth). */
  error: PhotoError | null;
}

/**
 * Photo slot — 140×140 rounded square with state-aware rendering:
 *   - empty: dashed border + "+" button
 *   - validating: dim overlay + spinner while we run sync+async checks
 *   - filled (ok): photo background + remove button
 *   - error: red border + "!" icon + "Error" label (detailed copy lives
 *     in the parent's banner below the photos)
 *
 * Validation runs locally before the file is committed to wizard state.
 * If a check fails, we hold an error in the parent (`onError`) but DO NOT
 * commit the File — so the Continue CTA can't fire with a rejected photo.
 *
 * Two-stage validation:
 *   1. Sync (mime + size) — instant.
 *   2. Async ML (face count + face area + sunglasses) — lazy-loads
 *      face-api.js + @huggingface/transformers on first call. ~1-3s
 *      warm-up, then ~200-500ms per check. On ML failure (CDN dead,
 *      browser too old) we accept the file optimistically — server is
 *      the final gate.
 *
 * See lib/photoValidation.ts for the full rule set + graceful-failure
 * policy.
 */
export function PhotoSlot({ label, file, onFileSelect, onError, error }: PhotoSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  // Object URL lifecycle (revoke on unmount / file change to avoid leak).
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    // Allow re-selecting the same file (input.onchange doesn't fire for
    // same path) — clear the input each time after we read it.
    if (inputRef.current) inputRef.current.value = '';

    if (!picked) return;

    // Stage 1 — synchronous gates (mime + size). Reject without committing.
    const sync = validatePhotoSync(picked);
    if (!sync.ok) {
      onFileSelect(null);
      onError(sync.error);
      return;
    }

    // Stage 2 — async ML (face-api + HF). While this runs we commit the
    // file optimistically (preview shows immediately) but mark validating
    // so parent can disable Continue. If ML rejects we then revert.
    onFileSelect(picked);
    onError(null);
    setValidating(true);
    try {
      const result = await validatePhotoAsync(picked);
      if (result.ok) {
        // Either confirmed face or API unavailable — both pass-through.
        // Nothing to do; file stays committed, no error.
      } else {
        onFileSelect(null);
        onError(result.error);
      }
    } finally {
      setValidating(false);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect(null);
    onError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, fontFamily }}>
      <input
        ref={inputRef}
        type="file"
        // Explicit MIME accept (not image/*) so the OS picker filters out
        // HEIC/WebP/etc before we even see them. We still re-check at
        // validation time because some Androids over-grant accept.
        accept="image/jpeg,image/png"
        onChange={handleChange}
        style={{ display: 'none' }}
      />

      {error ? (
        // Error visual — slot itself becomes the error indicator. Detailed
        // copy lives in the parent banner below (room for long i18n).
        <div
          onClick={openPicker}
          role="button"
          aria-label={`${label} — replace photo`}
          style={{
            width: 140,
            height: 140,
            borderRadius: 22,
            border: `2px solid ${tg.destructive}`,
            background: 'rgba(235, 85, 69, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              background: 'rgba(235, 85, 69, 0.15)',
              color: tg.destructive,
              fontSize: 22,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 6,
            }}
          >
            !
          </div>
          <div style={{ fontSize: 12, color: tg.destructive, fontWeight: 600 }}>
            Error
          </div>
        </div>
      ) : file && previewUrl ? (
        <div
          onClick={openPicker}
          role="button"
          aria-label={`Replace ${label} photo`}
          style={{
            width: 140,
            height: 140,
            borderRadius: 22,
            position: 'relative',
            overflow: 'hidden',
            backgroundImage: `url("${previewUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer',
            boxShadow: `0 4px 14px -3px ${brand.solid}66`,
          }}
        >
          {validating && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <TailSpin
                height={28}
                width={28}
                color="#fff"
                strokeWidth={3}
                ariaLabel="Validating photo"
              />
              <div style={{ fontSize: 11, marginTop: 8, opacity: 0.85 }}>Checking photo…</div>
            </div>
          )}
          {!validating && (
            <button
              onClick={handleRemove}
              aria-label={`Remove ${label} photo`}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 26,
                height: 26,
                borderRadius: 999,
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          )}
        </div>
      ) : (
        <div
          onClick={openPicker}
          role="button"
          aria-label={`Add ${label} photo`}
          style={{
            width: 140,
            height: 140,
            borderRadius: 22,
            border: `2px dashed ${tg.textHint}`,
            background: tg.bg2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              background: 'rgba(229, 64, 122, 0.12)',
              color: brand.solid,
              fontSize: 22,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 6,
            }}
          >
            +
          </div>
          <div style={{ fontSize: 12, color: tg.textHint, fontWeight: 500 }}>
            Tap to add
          </div>
        </div>
      )}

      <div style={{ fontSize: 13, color: tg.textHint, marginTop: 10, fontWeight: 500 }}>
        {label}
      </div>
    </div>
  );
}

/** Re-export for parents that want to read validation/animation state. */
export { type PhotoError };
